import { useCallback, useState } from "react";
import { API_URL } from "../../../../utils/common";
import { CmsBanner } from "../types";
import { CarruselSeleccion, ReordenItem } from "./reorden.types";

const getToken = () => localStorage.getItem("jwt");

/**
 * Hook para la vista de reordenamiento de carrusel.
 *
 * Lee los banners de `GET /cms/banners` (que ya vienen con sus distribuciones
 * ordenadas por `orden`) y expone `guardarOrden` para persistir un nuevo orden
 * mediante `PATCH /cms/banners/reordenar` (endpoint batch, transaccional).
 */
export function useReordenBanners() {
  const [banners, setBanners] = useState<CmsBanner[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchBanners = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/cms/banners`, {
        method: "GET",
        headers: { Authorization: `bearer ${token}` },
        cache: "no-store",
      });
      if (!res.ok) {
        throw new Error(`Error ${res.status} al obtener banners`);
      }
      const data: CmsBanner[] = await res.json();
      setBanners(Array.isArray(data) ? data : []);
      return data;
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Error desconocido";
      setError(msg);
      setBanners([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Persiste el nuevo orden de un carrusel. Envia la lista YA en el orden deseado;
   * el backend reasigna orden 1..N por posicion en una transaccion.
   * Devuelve true si se guardo correctamente, false si fallo.
   */
  const guardarOrden = useCallback(async (items: ReordenItem[]) => {
    setSaving(true);
    setError(null);
    try {
      const token = getToken();
      const body = {
        items: items.map((it, idx) => ({
          idDistribucion: it.idDistribucion,
          orden: idx + 1,
        })),
      };
      const res = await fetch(`${API_URL}/cms/banners/reordenar`, {
        method: "PATCH",
        headers: {
          Authorization: `bearer ${token}`,
          "content-type": "application/json",
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw new Error(`Error ${res.status} al guardar el orden`);
      }
      return true;
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Error al guardar";
      setError(msg);
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  return { banners, loading, error, saving, fetchBanners, guardarOrden };
}

const norm = (v: string | undefined | null) => (v ?? "").trim().toUpperCase();

/**
 * Deriva las opciones de filtro REALES a partir de los banners cargados
 * (solo los de tipoVista = CARRUSEL). Devuelve, para la seleccion parcial actual,
 * las combinaciones disponibles en cada nivel. Asi el admin solo ve carruseles
 * que existen en la BD (incluye SKY_WEB, APP_DEMO, etc.), no un catalogo estatico.
 */
export function opcionesDeCarrusel(
  banners: CmsBanner[],
  parcial: Partial<CarruselSeleccion>
) {
  const carruseles = banners.filter(
    (b) => b.activo && norm(b.tipoVista) === "CARRUSEL"
  );

  const uniq = (arr: string[]) =>
    Array.from(new Set(arr.filter((v) => v && v.trim() !== ""))).sort();

  const tipoAplicacion = uniq(carruseles.map((b) => b.tipoAplicacion));

  const tipo = uniq(
    carruseles
      .filter(
        (b) =>
          !parcial.tipoAplicacion ||
          norm(b.tipoAplicacion) === norm(parcial.tipoAplicacion)
      )
      .map((b) => b.tipo)
  );

  const seccion = uniq(
    carruseles
      .filter(
        (b) =>
          (!parcial.tipoAplicacion ||
            norm(b.tipoAplicacion) === norm(parcial.tipoAplicacion)) &&
          (!parcial.tipo || norm(b.tipo) === norm(parcial.tipo))
      )
      .map((b) => b.seccion)
  );

  const formato = uniq(
    carruseles
      .filter(
        (b) =>
          (!parcial.tipoAplicacion ||
            norm(b.tipoAplicacion) === norm(parcial.tipoAplicacion)) &&
          (!parcial.tipo || norm(b.tipo) === norm(parcial.tipo)) &&
          (!parcial.seccion || norm(b.seccion) === norm(parcial.seccion))
      )
      .map((b) => b.formato)
  );

  return { tipoAplicacion, tipo, seccion, formato };
}

/**
 * A partir de la lista de banners y una seleccion de carrusel, arma la lista de
 * items a reordenar: recorre cada banner de la combinacion y aplana sus
 * distribuciones activas en tarjetas ReordenItem, ordenadas por `orden`.
 *
 * Un "carrusel" = misma (tipoAplicacion, tipo, seccion, formato) con tipoVista CARRUSEL.
 */
export function construirItemsDeCarrusel(
  banners: CmsBanner[],
  seleccion: CarruselSeleccion
): ReordenItem[] {
  const items: ReordenItem[] = [];
  banners
    .filter(
      (b) =>
        b.activo &&
        norm(b.tipoVista) === "CARRUSEL" &&
        norm(b.tipoAplicacion) === norm(seleccion.tipoAplicacion) &&
        norm(b.tipo) === norm(seleccion.tipo) &&
        norm(b.seccion) === norm(seleccion.seccion) &&
        norm(b.formato) === norm(seleccion.formato)
    )
    .forEach((b) => {
      (b.distribuciones || [])
        .filter((d) => d.activo)
        .forEach((d) => {
          items.push({
            idDistribucion: d.id,
            idBanner: b.id,
            titulo: b.titulo,
            urlBanner: b.urlBanner,
            orden: d.orden,
          });
        });
    });

  // Orden estable por 'orden' asc; desempate por idDistribucion.
  return items
    .sort((a, b) => a.orden - b.orden || a.idDistribucion - b.idDistribucion)
    .map((it, idx) => ({ ...it, orden: idx + 1 }));
}
